import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  CloudSun, Sun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning,
  Thermometer, Droplets, Sparkles, MapPin, AlertTriangle, Gauge, RefreshCw,
} from "lucide-react";

const WICON = { sun: Sun, "cloud-sun": CloudSun, cloud: Cloud, fog: CloudFog, drizzle: CloudDrizzle, rain: CloudRain, snow: CloudSnow, storm: CloudLightning };
export const IRO_LABELS = { verde: "Baixo", amarelo: "Moderado", laranja: "Alto", vermelho: "Crítico" };
export const IRO_COLORS = {
  verde: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  amarelo: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  laranja: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  vermelho: "bg-red-500/15 text-red-400 border-red-500/30",
};
const IRO_TEXT = { verde: "text-emerald-400", amarelo: "text-amber-400", laranja: "text-orange-400", vermelho: "text-red-400" };
const IRO_BORDER = { verde: "border-emerald-500/40", amarelo: "border-amber-500/40", laranja: "border-orange-500/50", vermelho: "border-red-500/60" };
const IRO_BAR = { verde: "bg-emerald-500", amarelo: "bg-amber-500", laranja: "bg-orange-500", vermelho: "bg-red-500" };
const ALERT_CLS = {
  amarelo: "border-amber-500/40 bg-amber-500/10 text-amber-700",
  laranja: "border-orange-500/40 bg-orange-500/10 text-orange-700",
  vermelho: "border-red-500/50 bg-red-500/10 text-red-700",
};
const FACTOR_LABELS = { clima: "Clima (7 dias)", sazonalidade: "Sazonalidade", estrutural: "Estrutural", historico: "Histórico" };

function factorLevel(v) {
  if (v >= 80) return "vermelho";
  if (v >= 60) return "laranja";
  if (v >= 40) return "amarelo";
  return "verde";
}

function WeatherIcon({ icon, size = 16, className = "" }) {
  const I = WICON[icon] || Cloud;
  return <I size={size} className={className} />;
}

function GeoDialog({ unit, onClose, onSaved }) {
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const save = async () => {
    if (lat === "" || lng === "") return toast.error("Informe latitude e longitude");
    try {
      await api.put(`/environment/units/${unit.unit_id}/geo`, { lat: parseFloat(lat), lng: parseFloat(lng) });
      toast.success("Localização da unidade salva");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <Dialog open={!!unit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="env-geo-dialog">
        <DialogHeader><DialogTitle className="font-heading">Localização — {unit?.unit_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Defina as coordenadas da unidade para habilitar a previsão climática via Open-Meteo (sem chave de API).</p>
        <div className="grid grid-cols-2 gap-3">
          <Input type="number" step="any" placeholder="Latitude" value={lat} onChange={(e) => setLat(e.target.value)} data-testid="env-geo-lat-input" />
          <Input type="number" step="any" placeholder="Longitude" value={lng} onChange={(e) => setLng(e.target.value)} data-testid="env-geo-lng-input" />
        </div>
        <Button className="w-full" onClick={save} data-testid="env-geo-save-btn">Salvar Localização</Button>
      </DialogContent>
    </Dialog>
  );
}

function Md({ text }) {
  return (
    <div className="space-y-1.5 text-sm">
      {String(text).split("\n").map((ln, i) => {
        const t = ln.trim();
        if (!t) return null;
        if (t.startsWith("## ")) return <h4 key={i} className="font-heading font-semibold text-sm text-primary mt-3">{t.slice(3).replace(/\*\*/g, "")}</h4>;
        if (t.startsWith("# ")) return <h4 key={i} className="font-heading font-semibold mt-3">{t.slice(2).replace(/\*\*/g, "")}</h4>;
        if (t.startsWith("- ") || t.startsWith("* ") || t.startsWith("• ")) return <p key={i} className="pl-3 text-muted-foreground">• {t.slice(2).replace(/\*\*/g, "")}</p>;
        return <p key={i} className="text-muted-foreground">{t.replace(/\*\*/g, "").replace(/^#+\s*/, "")}</p>;
      })}
    </div>
  );
}

function AiDialog({ open, onOpenChange, clientId, unitId }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/environment/ai-analysis", {
        client_id: clientId !== "todos" ? clientId : null,
        unit_id: unitId !== "todas" ? unitId : null,
      });
      setAnalysis(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="env-ai-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Sparkles size={17} className="text-primary" /> Análise IA — Inteligência Ambiental e Sazonalidade</DialogTitle>
        </DialogHeader>
        {!analysis ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">A IA correlacionará a previsão climática de 7 dias (Open-Meteo), sazonalidade do mês, não conformidades abertas e histórico para indicar: pragas esperadas, unidades prioritárias, ações preventivas e a melhor janela operacional.</p>
            <Button className="w-full" onClick={generate} disabled={loading} data-testid="env-ai-generate-btn">
              <Sparkles size={15} className="mr-2" /> {loading ? "Correlacionando clima, sazonalidade e histórico..." : "Gerar Análise IA"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div data-testid="env-ai-content"><Md text={analysis.content} /></div>
            <Button variant="outline" className="w-full" onClick={generate} disabled={loading} data-testid="env-ai-regenerate-btn">
              {loading ? "Analisando..." : "Gerar nova análise"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function EnvironmentIntelligence() {
  const { user, can } = useAuth();
  const canSetGeo = can("geoespacial", "edit"); // mesma regra da rota de localização
  const [clients, setClients] = useState([]);
  const [units, setUnits] = useState([]);
  const [clientId, setClientId] = useState("todos");
  const [unitId, setUnitId] = useState("todas");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [geoUnit, setGeoUnit] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (clientId !== "todos") params.client_id = clientId;
    if (unitId !== "todas") params.unit_id = unitId;
    api.get("/environment/risk", { params }).then(({ data: d }) => setData(d)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user.role !== "cliente") api.get("/clients").then(({ data: d }) => setClients(d)).catch(() => {});
  }, [user.role]);

  useEffect(() => {
    if (clientId !== "todos") api.get("/units", { params: { client_id: clientId } }).then(({ data: d }) => setUnits(d));
    else setUnits([]);
    setUnitId("todas");
  }, [clientId]);

  useEffect(() => { load(); }, [clientId, unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const s = data?.summary;

  return (
    <div className="space-y-6" data-testid="env-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <CloudSun size={26} className="text-sky-400" /> Inteligência Ambiental
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Índice de Risco Operacional (0-100) por unidade: clima em tempo real (Open-Meteo) × sazonalidade{s?.mes ? ` de ${s.mes}` : ""} × não conformidades × histórico.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user.role !== "cliente" && (
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-44 h-9" data-testid="env-client-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os clientes</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={unitId} onValueChange={setUnitId} disabled={clientId === "todos"}>
            <SelectTrigger className="w-44 h-9" data-testid="env-unit-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as unidades</SelectItem>
              {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={load} title="Atualizar" data-testid="env-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
          {user.role !== "cliente" && (
            <Button variant="outline" onClick={() => setAiOpen(true)} data-testid="env-ai-btn">
              <Sparkles size={15} className="mr-2 text-primary" /> Análise IA
            </Button>
          )}
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center h-64"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            <Card className={`p-3 border-2 bg-card xl:col-span-2 ${IRO_BORDER[s.level]}`} data-testid="env-kpi-media">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Risco operacional médio</p>
                <Gauge size={14} className={IRO_TEXT[s.level]} />
              </div>
              <p className={`font-heading text-3xl font-bold mt-1 ${IRO_TEXT[s.level]}`}>{s.media}<span className="text-sm text-muted-foreground font-normal">/100</span></p>
            </Card>
            {["verde", "amarelo", "laranja", "vermelho"].map((k) => (
              <Card key={k} className={`p-3 border bg-card ${IRO_BORDER[k]}`} data-testid={`env-count-${k}`}>
                <p className={`font-heading text-2xl font-bold ${IRO_TEXT[k]}`}>{s.counts?.[k] || 0}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{IRO_LABELS[k]}</p>
              </Card>
            ))}
            <Card className="p-3 border-border bg-card" data-testid="env-kpi-alertas">
              <p className="font-heading text-2xl font-bold text-amber-400">{s.alertas}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">Alertas ativos</p>
            </Card>
            <Card className="p-3 border-border bg-card" data-testid="env-kpi-roedores">
              <p className="font-heading text-2xl font-bold">{s.roedores?.total ?? 0}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                Roedores {s.roedores?.vivos > 0 ? `({s.roedores.vivos} vivos)` : ""}
              </p>
            </Card>
            <Card className="p-3 border-border bg-card" data-testid="env-kpi-unidades">
              <p className="font-heading text-2xl font-bold">{s.total}<span className="text-sm text-muted-foreground font-normal"> unid.</span></p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{s.sem_geo > 0 ? `${s.sem_geo} sem localização` : "Todas georreferenciadas"}</p>
            </Card>
          </div>

          {data.units.length === 0 ? (
            <Card className="p-10 border-border bg-card text-center">
              <CloudSun size={32} className="mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-3">Nenhuma unidade encontrada nos filtros atuais.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {data.units.map((u) => (
                <Card key={u.unit_id} className={`p-5 border-2 bg-card ${IRO_BORDER[u.level]}`} data-testid={`env-unit-card-${u.unit_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-heading font-semibold truncate">{u.unit_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.client_name}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {u.weather?.current && (
                        <div className="flex items-center gap-2 text-sm" data-testid={`env-current-${u.unit_id}`}>
                          <WeatherIcon icon={u.weather.current.icon} size={18} className="text-sky-400" />
                          <span className="flex items-center gap-0.5"><Thermometer size={12} className="text-orange-400" />{Math.round(u.weather.current.temp)}°C</span>
                          <span className="flex items-center gap-0.5"><Droplets size={12} className="text-sky-400" />{Math.round(u.weather.current.humidity)}%</span>
                        </div>
                      )}
                      <div className="text-center">
                        <p className={`font-heading text-3xl font-bold leading-none ${IRO_TEXT[u.level]}`} data-testid={`env-iro-${u.unit_id}`}>{u.iro}</p>
                        <Badge variant="outline" className={`${IRO_COLORS[u.level]} mt-1 text-[10px]`}>{IRO_LABELS[u.level]}</Badge>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-5 gap-y-2 mt-4">
                    {Object.entries(u.factors).map(([k, v]) => (
                      <div key={k} data-testid={`env-factor-${k}-${u.unit_id}`}>
                        <div className="flex items-center justify-between text-[11px] mb-1">
                          <span className="text-muted-foreground">{FACTOR_LABELS[k]}</span>
                          <span className="font-semibold">{v}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted">
                          <div className={`h-full rounded-full ${IRO_BAR[factorLevel(v)]}`} style={{ width: `${v}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {u.weather?.daily?.length > 0 && (
                    <div className="flex gap-1 mt-4 overflow-x-auto scrollbar-thin" data-testid={`env-forecast-${u.unit_id}`}>
                      {u.weather.daily.map((d) => (
                        <div key={d.date} className="flex-1 min-w-[62px] rounded-md border border-border/60 p-1.5 text-center" title={`${d.label} · ${d.rain} mm`}>
                          <p className="text-[9px] uppercase text-muted-foreground">{new Date(d.date + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")} {d.date.slice(8, 10)}</p>
                          <WeatherIcon icon={d.icon} size={15} className="mx-auto my-1 text-sky-400" />
                          <p className="text-[10px] font-semibold">{Math.round(d.tmax)}°<span className="text-muted-foreground font-normal">/{Math.round(d.tmin)}°</span></p>
                          <p className="text-[9px] text-sky-400">{d.rain > 0 ? `${Math.round(d.rain)}mm` : "—"}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {u.sem_geo && (
                    <div className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 flex items-center justify-between gap-2" data-testid={`env-no-geo-${u.unit_id}`}>
                      <p className="text-xs text-amber-700 flex items-center gap-1.5"><MapPin size={13} /> Sem localização — clima indisponível (fator neutro aplicado).</p>
                      {canSetGeo && (
                        <Button variant="outline" size="sm" className="h-7 text-xs shrink-0" onClick={() => setGeoUnit(u)} data-testid={`env-set-geo-btn-${u.unit_id}`}>
                          Definir localização
                        </Button>
                      )}
                    </div>
                  )}

                  {u.alerts.length > 0 && (
                    <div className="space-y-1.5 mt-4" data-testid={`env-alerts-${u.unit_id}`}>
                      {u.alerts.map((a, i) => (
                        <p key={i} className={`text-xs rounded-md border px-3 py-2 flex gap-2 ${ALERT_CLS[a.severity] || ALERT_CLS.amarelo}`}>
                          <AlertTriangle size={13} className="shrink-0 mt-0.5" /> {a.text}
                        </p>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <GeoDialog unit={geoUnit} onClose={() => setGeoUnit(null)} onSaved={load} />
      <AiDialog open={aiOpen} onOpenChange={setAiOpen} clientId={clientId} unitId={unitId} />
    </div>
  );
}
